import React, { FC, useMemo, useCallback } from "react";
import { Tag } from "@foris/avocado-suite";
import { Catalog } from "@models/ISchema";
import styles from "./resourceFilter.module.scss";

interface CatalogItem extends Catalog {
  category: string;
}

interface ResourceFilterProps {
  data: CatalogItem[];
  selectedResource: string | null;
  searchQuery?: string;
  onFilterChange: (category: string | null) => void;
  label: string;
}

const ResourceFilter: FC<ResourceFilterProps> = ({
  data,
  searchQuery,
  selectedResource,
  onFilterChange,
  label,
}) => {
  const totalBySearchQuery = useMemo(() => {
    if (!searchQuery) {
      return data?.length ?? 0;
    }

    return (data ?? []).filter(item =>
      item?.label?.toLowerCase().includes(searchQuery?.toLowerCase() ?? ""),
    ).length;
  }, [data, searchQuery]);

  const categories = useMemo(() => {
    return (data ?? []).reduce((acc: Record<string, number>, item) => {
      const category = item.category;
      const itemLabel = item?.label;
      const isSearchQuery = !!searchQuery
        ? itemLabel?.toLowerCase().includes(searchQuery?.toLowerCase() ?? "")
        : true;

      if (category) {
        acc[category] = (acc[category] || 0) + (isSearchQuery ? 1 : 0);
      }

      return acc;
    }, {});
  }, [data, searchQuery]);

  const handleFilterClick = useCallback(
    (category: string | null) => {
      onFilterChange(category);
    },
    [onFilterChange],
  );

  return (
    <div className={styles.resourceFilter}>
      <Tag active={selectedResource === null} onClick={() => handleFilterClick(null)}>
        {label} ({totalBySearchQuery})
      </Tag>
      {Object.entries(categories).map(([category, count]) => (
        <Tag
          active={selectedResource === category}
          disabled={count === 0 && selectedResource !== category}
          key={category}
          onClick={() =>
            !(count === 0 && selectedResource !== category) && handleFilterClick(category)
          }
        >
          {category} ({count})
        </Tag>
      ))}
    </div>
  );
};

export default ResourceFilter;
